import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Tag, Clock } from 'lucide-react';
import DOMPurify from 'dompurify';
import blogContent from '../data/blogContent';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { slug } = useParams();
  const [apiPost, setApiPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchArticle = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/blog/articles/${slug}`);
      if (res.ok) {
        const data = await res.json();
        setApiPost(data);
      }
    } catch {
      // API unavailable - fall back to static
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchRelated = useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/blog/articles?limit=20`);
      if (res.ok) {
        const data = await res.json();
        const others = (data.articles || []).filter(a => a.slug !== slug).slice(0, 3);
        setRelated(others);
      }
    } catch {
      // No related — skip
    }
  }, [slug]);

  useEffect(() => {
    fetchArticle();
    fetchRelated();
  }, [fetchArticle, fetchRelated]);

  // Use API post if available, otherwise fall back to static
  const staticPost = blogContent[slug];
  const post = apiPost || staticPost;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading article...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Helmet>
          <title>Blog Post Not Found | CashifyGCMart</title>
          <meta name="description" content="This blog post is not available. Browse our latest gift card selling tips and guides." />
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
          <meta name="prerender-status-code" content="404" />
          <link rel="canonical" href="https://cashifygcmart.com/blog" />
        </Helmet>
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404 — Post Not Found</h1>
          <p className="text-gray-600 mb-6">The blog post you're looking for doesn't exist or has been moved.</p>
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-pink-600 font-semibold hover:text-pink-700"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // Normalize fields between API and static formats
  const title = post.title || '';
  const content = post.content || '';
  const tags = post.tags || [];
  const date = post.publish_date || post.date || '';
  const readTime = post.read_time ? `${post.read_time} min read` : (post.readTime || '8 min read');
  const author = post.author || 'CashifyGCMart Team';
  const excerpt = post.excerpt || content.substring(0, 160);
  const metaDesc = post.meta_description || excerpt;
  const canonicalUrl = `https://cashifygcmart.com/blog/${slug}`;
  // Keep <title> under 60 chars for search-result display
  const suffix = ' | Cashifygcmart';
  const maxTitleLen = 60;
  const trimmedTitle = title.length + suffix.length <= maxTitleLen
    ? `${title}${suffix}`
    : `${title.slice(0, maxTitleLen - suffix.length - 1).trimEnd()}…${suffix}`;
  const rawImage = post.image_url || post.image || '';
  const ogImage = rawImage
    ? (rawImage.startsWith('http') ? rawImage : `https://cashifygcmart.com${rawImage}`)
    : 'https://cashifygcmart.com/images/og-cover.jpg';

  const blogPostingSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": metaDesc,
    "image": ogImage,
    "author": { "@type": "Organization", "name": author },
    "publisher": {
      "@type": "Organization",
      "name": "CashifyGCMart",
      "logo": { "@type": "ImageObject", "url": "https://cashifygcmart.com/favicon.svg" }
    },
    "datePublished": date || undefined,
    "dateModified": post.updated_at || date || undefined,
    "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalUrl },
    "keywords": (tags || []).join(', ')
  };

  return (
    <>
      <Helmet>
        <title>{trimmedTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={trimmedTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:site_name" content="CashifyGCMart" />
        {date && <meta property="article:published_time" content={date} />}
        {(post.updated_at || date) && (
          <meta property="article:modified_time" content={post.updated_at || date} />
        )}
        {tags.map(tag => <meta key={tag} property="article:tag" content={tag} />)}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={trimmedTitle} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">
          {JSON.stringify(blogPostingSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-12">
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-8 transition"
            data-testid="back-to-blog-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>

          <header className="mb-8">
            {/* Hero image */}
            {(post.image_url || post.image) && (
              <div className="relative h-64 md:h-96 overflow-hidden rounded-xl mb-6">
                <img
                  src={post.image_url || post.image}
                  alt={title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/30 to-transparent" />
              </div>
            )}

            <div className="flex gap-2 mb-4 flex-wrap">
              {tags.map(tag => (
                <span 
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-medium"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                </span>
              ))}
              {post.generated_by === 'ai' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  AI Generated
                </span>
              )}
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{title}</h1>

            <div className="flex items-center gap-6 text-gray-600 flex-wrap">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {date && new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {readTime}
              </div>
              <div>By {author}</div>
            </div>
          </header>

          <div 
            className="prose prose-lg max-w-none
              prose-headings:text-gray-900 prose-headings:font-bold
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
              prose-ul:my-6 prose-ul:list-disc prose-ul:pl-6
              prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6
              prose-li:text-gray-700 prose-li:mb-2
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-a:text-pink-600 prose-a:no-underline hover:prose-a:underline
              prose-table:border-collapse prose-table:w-full
              prose-th:border prose-th:border-gray-300 prose-th:bg-gray-100 prose-th:p-3
              prose-td:border prose-td:border-gray-300 prose-td:p-3"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
            data-testid="blog-article-content"
          />

          <div className="mt-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg p-8 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Sell Your Gift Cards?</h2>
            <p className="text-xl mb-6 opacity-90">Get instant quotes and same-day payouts</p>
            <Link 
              to="/form-submission"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
              data-testid="blog-cta-sell-button"
            >
              Start Selling Now
            </Link>
          </div>

          <div className="mt-8 text-center">
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-pink-600 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
          </div>

          {/* Related articles — internal linking for crawlability */}
          {related.length > 0 && (
            <section className="mt-16 border-t border-gray-200 pt-10" data-testid="related-articles">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">You might also like</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {related.map(r => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow p-5 border border-gray-100"
                    data-testid={`related-article-${r.slug}`}
                  >
                    {r.image_url && (
                      <img
                        src={r.image_url}
                        alt={r.title}
                        loading="lazy"
                        className="w-full h-32 object-cover rounded mb-3"
                      />
                    )}
                    <h3 className="font-semibold text-gray-900 mb-2 leading-snug">
                      {r.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {r.excerpt || ''}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </div>
    </>
  );
};

export default BlogPost;
